import React, { useState } from 'react';
import { PiPlay } from 'react-icons/pi';
import { MediaModal } from '../../atoms/media/media-modal';
import { MediaSkeleton } from '../../atoms/media/media-skeleton';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { MediaErrorSkeleton } from '../../atoms/media/media-error-skeleton';

interface VideoMediaProps {
  url: string;
  isLoading?: boolean;
  className?: string;
  footer?: React.ReactNode;
}

export const VideoMedia: React.FC<VideoMediaProps> = ({
  url,
  isLoading = false,
  className = '',
  footer = null,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);

  const handleReady = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = event.currentTarget;
    const aspectRatio = video.videoWidth / video.videoHeight;
    setIsLandscape(aspectRatio > 1);
    setIsReady(true);
    setHasError(false);
  };

  const handleError = () => {
    setIsReady(false);
    setHasError(true);
  };

  const getDynamicContainerClasses = (): string => {
    const baseClasses = 'flex relative w-full h-auto rounded-lg';
    const sizeClasses = isLandscape
      ? 'max-w-[27rem] max-h-[14rem]'
      : 'max-w-[14rem] max-h-[27rem]';
    return `${baseClasses} ${sizeClasses} ${className}`;
  };

  const containerClasses = getDynamicContainerClasses();
  const playOverlayClasses = `
    absolute w-full h-full inset-0 flex items-center justify-center 
    cursor-pointer group
  `;
  const videoLoading = (isLoading || !isReady) && !hasError;

  return (
    <>
      <div className="flex flex-col w-full gap-2 justify-start">
        <div className={containerClasses}>
          {videoLoading && <MediaSkeleton type="video" />}
          {hasError && <MediaErrorSkeleton type="video" />}
          <div className="relative w-full h-full">
            <video
              key={url}
              onClick={() => !videoLoading && !hasError && setIsModalOpen(true)}
              muted
              onLoadedMetadata={handleReady}
              onError={handleError}
              className={`w-full h-full rounded-lg cursor-pointer object-cover ${
                isReady ? 'block' : 'hidden'
              }`}
            >
              <source src={url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            {isReady && !hasError && (
              <div
                className={playOverlayClasses}
                onClick={() => setIsModalOpen(true)}
              >
                <Button
                  variant="secondary"
                  appearance="soft"
                  size="md"
                  className="group-hover:bg-secondary-100"
                >
                  <PiPlay className="w-4 h-4 text-primary-600" />
                </Button>
              </div>
            )}
          </div>
        </div>

        {footer && footer}
      </div>

      <MediaModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        {videoLoading && <MediaSkeleton type="video" />}
        {hasError && <MediaErrorSkeleton />}
        <video
          key={url}
          controls
          autoPlay
          onLoadedMetadata={handleReady}
          onError={handleError}
          className={`max-w-full max-h-full rounded-lg ${isReady ? 'block' : 'hidden'}`}
        >
          <source src={url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </MediaModal>
    </>
  );
};
